import { FaPhone, FaWhatsapp, FaHeadset, FaClock, FaCheckCircle, FaUserTie, FaStar, FaComments, FaArrowRight, FaShieldAlt, FaHeart } from 'react-icons/fa';
import { HiSparkles, HiLightningBolt } from 'react-icons/hi';
import { BsLightningChargeFill, BsTelephoneFill, BsWhatsapp } from 'react-icons/bs';
import { IoMdRocket } from 'react-icons/io';

function ContactSection() {
  const features = [
    { icon: FaClock, text: "Quick Response Time" },
    { icon: FaCheckCircle, text: "Personalized Guidance" },
    { icon: FaShieldAlt, text: "100% Confidential" },
    { icon: FaHeart, text: "Dedicated Support" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-indigo-600 to-violet-700 rounded-3xl p-8 sm:p-12 mb-10">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-indigo-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        
        {/* Decorative Icons */}
        <div className="absolute top-8 right-20 text-white/10 text-6xl transform rotate-12">
          <FaComments />
        </div>
        <div className="absolute bottom-12 left-16 text-white/10 text-5xl transform -rotate-12">
          <FaPhone />
        </div>
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">
            <HiLightningBolt className="text-amber-400" />
            <span>Get Expert Help Now</span>
            <HiSparkles className="text-amber-400" />
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Need Help? 
            <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent"> Contact RK</span>
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Have questions about the course or need personalized guidance? Get direct access to expert support.
          </p>
        </div>
        
        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
          {/* Left Side - Profile Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
            {/* Profile Image */}
            <div className="relative w-28 h-28 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl transform rotate-6 animate-pulse"></div>
              <div className="relative w-full h-full bg-gradient-to-br from-amber-400 to-amber-500 rounded-3xl flex items-center justify-center text-4xl font-bold text-slate-900 shadow-2xl shadow-amber-500/30">
                RK
              </div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center border-4 border-purple-600">
                <FaCheckCircle className="text-white text-sm" />
              </div>
            </div>
            
            {/* Name & Title */}
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
                Rushneek Singh
                <FaStar className="text-amber-400 text-lg" />
              </h3>
              <p className="text-white/70 flex items-center justify-center gap-2">
                <FaUserTie className="text-amber-400" />
                B2B Payment Recovery Expert
              </p>
              <div className="flex items-center justify-center gap-4 mt-3">
                <span className="text-sm text-white/60 flex items-center gap-1">
                  <BsLightningChargeFill className="text-amber-400" />
                  10,000+ Trained
                </span>
                <span className="text-sm text-white/60 flex items-center gap-1">
                  <FaStar className="text-amber-400" />
                  15+ Years Exp
                </span>
              </div>
            </div>
            
            {/* Feature Pills */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-xl text-white/80 text-sm"
                >
                  <feature.icon className="text-amber-400 flex-shrink-0" />
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
            
            {/* Availability Badge */}
            <div className="flex items-center justify-center gap-2 text-sm text-white/70 bg-green-500/20 py-2 rounded-full">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              Available Now • Usually responds within 1 hour
            </div>
          </div>
          
          {/* Right Side - Contact Buttons */}
          <div className="space-y-5">
            {/* Call Button */}
            <a 
              href="tel:+919876543210" 
              className="group flex items-center gap-4 bg-white text-purple-700 px-6 py-5 rounded-2xl font-semibold hover:bg-purple-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <BsTelephoneFill className="text-white text-xl" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold">Call Now</div>
                <div className="text-purple-500 text-sm">Direct phone support</div>
              </div>
              <FaArrowRight className="text-purple-400 group-hover:translate-x-2 transition-transform" />
            </a>
            
            {/* WhatsApp Button */}
            <a 
              href="https://wa.me/919876543210" 
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-5 rounded-2xl font-semibold hover:from-green-600 hover:to-emerald-600 transition-all duration-300 shadow-xl shadow-green-500/30 hover:shadow-2xl hover:-translate-y-1"
            >
              <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <BsWhatsapp className="text-white text-2xl" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold">WhatsApp Chat</div>
                <div className="text-white/80 text-sm">Quick message support</div>
              </div>
              <FaArrowRight className="text-white/80 group-hover:translate-x-2 transition-transform" />
            </a>
            
            {/* Schedule Call */}
            <div className="group flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-5 rounded-2xl font-semibold hover:bg-white/15 transition-all duration-300 cursor-pointer">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <FaClock className="text-slate-900 text-xl" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold">Schedule a Call</div>
                <div className="text-white/60 text-sm">Book a convenient time</div>
              </div>
              <FaArrowRight className="text-white/60 group-hover:translate-x-2 transition-transform" />
            </div>
            
            {/* Trust Badge */}
            <div className="flex items-center justify-center gap-6 pt-4">
              <div className="flex items-center gap-1 text-white/60 text-sm">
                <FaShieldAlt className="text-amber-400" />
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center gap-1 text-white/60 text-sm">
                <FaCheckCircle className="text-green-400" />
                <span>No Spam</span>
              </div>
              <div className="flex items-center gap-1 text-white/60 text-sm">
                <IoMdRocket className="text-purple-400" />
                <span>Fast Reply</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
